import { Controller } from "@hotwired/stimulus"

// Atajos de teclado V2 (estilo GitHub, para gente avanzada). No se anuncian:
// el panel de ayuda solo aparece al pulsar `?` (Shift + signo de pregunta).
//
// Vive en <body>. Tres capas, todas DECLARADAS en el HTML del server — este
// controller no conoce el dominio:
//
// 1. Atajos globales: los trae el <template> de V2::ShortcutsComponent (cada
//    fila con data-shortcut-keys/-run/-target) — ir a una vista (`m h`),
//    tema, menú, buscador, paginador. Las secuencias `m h`, `m s`… (m de
//    menú: los mismos destinos que el menú del navbar). `m` sola ABRE el
//    menú, que queda como prefijo visible: con él abierto, la letra navega,
//    ↓/↑ recorren sus opciones, Enter sigue la enfocada y Esc lo cierra.
//
// 2. Cursor de elementos: todo `[data-shortcut-item]` (fila de tabla, ítem de
//    lista, card de traspaso, fila de equipo del tablero) es alcanzable con
//    j/k (o ↓/↑). El elemento bajo el cursor lleva `data-shortcut-current`
//    (el CSS lo enmarca). Enter lo abre: hace click en su
//    `[data-shortcut-open]` (el link de la fila) o, sin él, en el elemento
//    mismo (las filas de tabla navegan por v2--row-link). Escape suelta el
//    cursor. `g g` y `G` van al primero y al último. `/` abre el buscador:
//    una cajita que filtra los elementos por nombre mientras se escribe y
//    deja el cursor en la primera coincidencia (↓/↑ recorren, Enter confirma).
//
//    El cursor y el FOCO van juntos: señalar un elemento lo enfoca
//    (tabindex=-1), así Tab entra directo a sus botones (link, editar,
//    eliminar) sin recorrer toda la página; y tabular hasta un elemento lo
//    señala, así j/k siguen desde ahí. ← / → recorren los botones y links
//    del elemento señalado; con uno enfocado, Enter lo dispara (nativo).
//
//    Flechas ESPACIALES: si los elementos declaran `data-shortcut-col`
//    (el tablero: transfer / equip / router), ↑/↓ recorren la misma columna
//    —equipo a equipo, saltando de traspaso en traspaso— y ←/→ cruzan de
//    columna dentro del traspaso: del equipo, ← es su traspaso y → el router
//    al que reporta (`data-shortcut-group` los empareja); del traspaso, → es
//    su primer equipo; del router, ← es su primera fila. Ahí las flechas no
//    recorren botones: para eso queda Tab.
//
// 3. Acciones: un botón con `data-shortcut-key="e"` (ButtonComponent
//    `shortcut:`) se dispara con esa tecla. Se busca primero en el elemento
//    bajo el cursor, luego en sus elementos ANCESTROS (una fila de equipo del
//    tablero no tiene "añadir equipo", pero su traspaso sí) y por último en
//    la página (botones fuera de todo elemento: "Añadir traspaso", "Editar"
//    de la ficha). Así `c` crea un equipo en el traspaso señalado, o un
//    traspaso sin cursor, y `d` elimina el equipo señalado — pasando por el
//    mismo v2--confirm que el click.
//
// El panel de ayuda lista las capas 2 y 3 leyendo el DOM al abrirse: sale lo
// que ESTA página ofrece, con permisos ya aplicados (un botón sin permiso no
// lleva data-shortcut-key).
//
// Secuencias al estilo `m h`: la primera tecla queda pendiente SEQUENCE_MS y
// la segunda resuelve.
//
// Nunca se dispara escribiendo: con el foco en un input/textarea/select o un
// contenteditable el teclado es del campo. Tampoco con Ctrl/Meta/Alt (son
// atajos del navegador), ni con un modal o confirm abierto (`.tp-modal`
// ajeno): ahí solo `?` sigue vivo.
//
// El panel se clona del <template> y se appendea al elemento en pantalla
// completa si lo hay (fuera de ese subárbol todo queda inerte — mismo caso
// que v2--confirm) o a <body>. Es transitorio: se remueve al cerrar y antes
// de que Turbo guarde la página en caché. El cursor es un atributo que el
// morphing de Turbo puede borrar y un nodo que la recarga del frame del
// tablero (cada 5 min) o un morph tras un alta pueden REEMPLAZAR (regla
// v2-tables-modals: el estado visible vive en el HTML). Por eso el cursor
// recuerda la IDENTIDAD del elemento (`data-shortcut-id`: la url de la fila,
// el código del router; o el id del nodo) y se re-resuelve contra el DOM
// vivo antes de cada movimiento y tras cada cambio del DOM: el usuario sigue
// donde estaba aunque el nodo sea otro. Si el elemento ya no existe, se suelta.
//
// Escape cierra SOLO este panel: no tiene formulario, así que el Escape del
// autorrelleno de Chrome no le aplica; y se escucha en captura deteniendo la
// propagación para que un modal de abajo (v2--modal también escucha Escape
// en document) no se cierre junto con la ayuda.
const SEQUENCE_MS = 1500
// Campos donde se escribe: ahí el teclado es del campo. Un checkbox, un
// radio o un botón NO escriben: los atajos siguen vivos con el foco en
// ellos (Tab desde una fila cae en su checkbox de selección).
const TYPING = "input:not([type=checkbox]):not([type=radio]):not([type=button]):not([type=submit]):not([type=reset]), textarea, select, [contenteditable]:not([contenteditable=false])"
const ITEM = "[data-shortcut-item]"
const CURRENT = "data-shortcut-current"
// Controles que se operan solos (Enter/Espacio nativos): con uno enfocado el
// cursor no les roba Enter, y ←/→ recorren a sus hermanos.
const INTERACTIVE = "a[href], button, input, select, textarea, summary, [role=tab]"
// Acción (botón, pestaña) que el buscador tiene seleccionada: se enmarca en
// la página con el mismo anillo que el cursor, para ver qué hará Enter.
// (No `data-shortcut-target`: ese es el destino de las filas del panel.)
const AIM = "data-shortcut-aim"
const MAX_RESULTS = 8

// Minúsculas y sin diacríticos, para comparar nombres tecleados a medias.
const normalize = (text) => text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim()

export default class extends Controller {
  static targets = ["template", "finder"]

  connect() {
    this.shortcuts = this.readShortcuts()
    this.pending = null
    this.panel = null
    this.current = null

    this._onKeydown = this.onKeydown.bind(this)
    document.addEventListener("keydown", this._onKeydown)
    // Turbo guarda un snapshot del body antes de navegar: ni el panel ni el
    // cursor deben quedar en él.
    this._onBeforeCache = () => { this.close(); this.closeFinder({ keep: false }); this.clearCursor() }
    document.addEventListener("turbo:before-cache", this._onBeforeCache)
    // Tras cualquier cambio del DOM (morph, recarga del frame del tablero,
    // stream) el atributo del cursor pudo perderse o su nodo ser otro: se
    // re-resuelve por identidad y se re-estampa. Con debounce: un render trae
    // cientos de mutaciones.
    this._observer = new MutationObserver(() => {
      clearTimeout(this._restoreTimer)
      this._restoreTimer = setTimeout(() => this.restoreCursor(), 50)
    })
    this._observer.observe(document.body, { childList: true, subtree: true })
    // Tab hasta un elemento (o hasta un botón suyo) lo señala.
    this._onFocusIn = this.onFocusIn.bind(this)
    document.addEventListener("focusin", this._onFocusIn)
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKeydown)
    document.removeEventListener("turbo:before-cache", this._onBeforeCache)
    this._observer.disconnect()
    clearTimeout(this._restoreTimer)
    document.removeEventListener("focusin", this._onFocusIn)
    this.close()
    this.closeFinder()
    this.clearCursor()
  }

  // { "g h": { run: "visit", target: "/v2" }, "t": { run: "click", target: ".tp-navbar__theme" }, … }
  // Las filas informativas (help/close/cursor) las resuelve este controller:
  // no entran al mapa.
  readShortcuts() {
    const map = new Map()
    if (!this.hasTemplateTarget) return map

    this.templateTarget.content.querySelectorAll("[data-shortcut-keys]").forEach((row) => {
      const run = row.dataset.shortcutRun
      if (!["visit", "click", "focus", "parent", "back"].includes(run)) return
      map.set(row.dataset.shortcutKeys, { run, target: row.dataset.shortcutTarget })
    })
    return map
  }

  onKeydown(event) {
    if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return
    // El buscador maneja sus propias teclas (finderKeydown); el resto de los
    // campos son de quien escribe.
    if (event.target.closest?.(TYPING)) return

    const key = event.key

    if (key === "?") {
      event.preventDefault()
      this.toggle()
      return
    }

    if (this.panel) {
      // Con la ayuda abierta la única tecla que hace algo es Escape.
      if (key === "Escape") {
        event.preventDefault()
        event.stopImmediatePropagation()
        this.close()
      }
      return
    }

    if (this.foreignModalOpen()) return

    // Menú abierto = prefijo `m` pulsado, sin plazo: la letra resuelve la
    // secuencia; ↓/↑ y Enter recorren y siguen sus opciones.
    if (this.menuOpen()) {
      if (this.menuKeydown(key, event)) return
      this.pending = { key: "m", at: Date.now() }
    }

    // Segunda tecla de una secuencia pendiente (`m` + `h`, `g` + `g`).
    if (this.pending && Date.now() - this.pending.at <= SEQUENCE_MS) {
      const combo = `${this.pending.key} ${key}`
      this.pending = null
      if (combo === "g g") {
        event.preventDefault()
        this.jump(0)
        return
      }
      if (this.run(combo, event)) {
        this.closeMenu()
        return
      }
    }
    this.pending = null

    if (this.moveCursor(key, event)) return
    if (this.runAction(key, event)) return
    if (this.selectTab(key, event)) return

    if (this.opensSequence(key)) {
      this.pending = { key, at: Date.now() }
      // `m`: el menú se abre como prefijo visible (ver menuKeydown).
      if (key === "m") {
        event.preventDefault()
        this.openMenu()
      }
      return
    }

    this.run(key, event)
  }

  opensSequence(key) {
    if (key === "g") return true // `g g` (primer elemento) vive en el controller
    const prefix = `${key} `
    for (const keys of this.shortcuts.keys()) {
      if (keys.startsWith(prefix)) return true
    }
    return false
  }

  // ── Capa 1: atajos globales ────────────────────────────────────────────

  // true si había atajo para `keys` (se ejecutó); false si no.
  run(keys, event) {
    const shortcut = this.shortcuts.get(keys)
    if (!shortcut) return false

    event.preventDefault()
    switch (shortcut.run) {
      case "visit":
        window.Turbo.visit(shortcut.target)
        break
      case "parent": {
        // El padre en la jerarquía: el último link del breadcrumb (el
        // segmento actual es texto plano). Sin breadcrumb, nada.
        const links = document.querySelectorAll(".tp-breadcrumb a[href]")
        const parent = links[links.length - 1]
        if (parent) this.activate(parent)
        break
      }
      case "back":
        window.history.back()
        break
      case "click": {
        const el = document.querySelector(shortcut.target)
        if (el) this.activate(el)
        break
      }
      case "focus": {
        const field = document.querySelector(shortcut.target)
        if (field) {
          field.focus()
          field.select?.()
        }
        break
      }
    }
    return true
  }

  // ── Menú del navbar como prefijo `m` ──────────────────────────────────

  menu() {
    return document.querySelector(".tp-navbar__menu")
  }

  menuOpen() {
    return Boolean(this.menu()?.open)
  }

  menuItems() {
    return Array.from(this.menu()?.querySelectorAll(".tp-navbar__panel :is(a[href], button)") || [])
      .filter((el) => el.getClientRects().length > 0)
  }

  openMenu() {
    const menu = this.menu()
    if (!menu || menu.open) return
    menu.open = true
    this.menuItems()[0]?.focus()
  }

  closeMenu() {
    const menu = this.menu()
    if (menu?.open) menu.open = false
  }

  // Teclas propias del menú abierto: ↓/↑ mueven el foco entre opciones,
  // Enter y Esc son nativos (el link navega; v2--menu cierra con Esc y
  // devuelve el foco al botón). true = resuelta acá.
  menuKeydown(key, event) {
    if (key === "ArrowDown" || key === "ArrowUp") {
      const items = this.menuItems()
      if (items.length === 0) return true
      const index = items.indexOf(document.activeElement)
      const delta = key === "ArrowDown" ? 1 : -1
      const next = index === -1 ? (delta > 0 ? 0 : items.length - 1) : (index + delta + items.length) % items.length
      event.preventDefault()
      items[next].focus()
      return true
    }
    if (key === "Enter" || key === "Escape" || key === "Tab") {
      this.pending = null
      return true
    }
    return false
  }

  // `1`…`9`: la pestaña N del bloque de pestañas de la página (los gráficos
  // del equipo y del router: presión, vida, vibración…). Sin pestañas, nada.
  selectTab(key, event) {
    if (!/^[1-9]$/.test(key)) return false
    const tabs = Array.from(document.querySelectorAll(".tp-tabs__list a")).filter((el) => el.getClientRects().length > 0)
    const tab = tabs[Number(key) - 1]
    if (!tab) return false

    event.preventDefault()
    this.activate(tab)
    return true
  }

  // ── Capa 2: cursor de elementos ────────────────────────────────────────

  // Elementos alcanzables: los que se ven (una fila plegada en un <details>
  // cerrado o un tab oculto no cuenta). En orden de documento.
  items() {
    return Array.from(document.querySelectorAll(ITEM)).filter((el) => el.getClientRects().length > 0)
  }

  moveCursor(key, event) {
    this.resolveCurrent()
    if ((key === "ArrowLeft" || key === "ArrowRight") && this.stepSiblings(key === "ArrowRight" ? 1 : -1, event)) return true
    if (key.startsWith("Arrow") && this.spatial(key, event)) return true

    switch (key) {
      case "j":
      case "ArrowDown":
        return this.step(1, event)
      case "k":
      case "ArrowUp":
        return this.step(-1, event)
      case "ArrowRight":
        return this.stepWithin(1, event)
      case "ArrowLeft":
        return this.stepWithin(-1, event)
      case "G":
        event.preventDefault()
        this.jump(-1)
        return true
      case "/":
        event.preventDefault()
        this.openFinder()
        return true
      case "Enter":
        return this.openCurrent(event)
      case "Escape":
        if (!this.current) return false
        event.preventDefault()
        this.clearCursor()
        return true
      default:
        return false
    }
  }

  step(delta, event) {
    const items = this.items()
    if (items.length === 0) return false

    event.preventDefault()
    let index = items.indexOf(this.current)
    if (index === -1) {
      index = delta > 0 ? 0 : items.length - 1
    } else {
      index = Math.min(items.length - 1, Math.max(0, index + delta))
    }
    this.setCursor(items[index])
    return true
  }

  // Primer (0) o último (-1) elemento visible.
  jump(index) {
    const items = this.items()
    if (items.length === 0) return
    this.setCursor(items.at(index))
  }

  // Señalar = marcar + enfocar: el foco viaja con el cursor para que Tab
  // siga desde el elemento (sus botones), no desde donde quedó antes. Con
  // el buscador abierto el foco se queda en su input: solo se marca.
  setCursor(item, { focus = true, scroll = true } = {}) {
    this.clearCursor()
    this.current = item
    this.currentId = item.dataset.shortcutId || item.id || null
    item.setAttribute(CURRENT, "")
    if (scroll) item.scrollIntoView({ block: "nearest" })
    if (focus && !this.finder) {
      if (!item.hasAttribute("tabindex")) item.setAttribute("tabindex", "-1")
      item.focus({ preventScroll: true })
    }
  }

  // Tab (o click) llegó a un elemento o a algo dentro de él: el cursor lo
  // sigue, sin robar el foco de donde aterrizó.
  onFocusIn(event) {
    const item = event.target.closest?.(ITEM)
    if (!item || item === this.current) return
    this.setCursor(item, { focus: false })
  }

  clearCursor() {
    document.querySelectorAll(`[${CURRENT}]`).forEach((el) => el.removeAttribute(CURRENT))
    this.current = null
    this.currentId = null
  }

  // El elemento señalado, contra el DOM vivo: si su nodo fue reemplazado, el
  // que hoy tiene su misma identidad. Sin identidad ni nodo, se suelta.
  resolveCurrent() {
    if (!this.current || this.current.isConnected) return
    const id = this.currentId
    const replacement = id && (
      document.querySelector(`${ITEM}[data-shortcut-id="${CSS.escape(id)}"]`) ||
      document.getElementById(id)
    )
    this.current = replacement?.matches(ITEM) ? replacement : null
    if (!this.current) this.currentId = null
  }

  // Tras un cambio del DOM: el cursor sigue en su elemento (o en su
  // reemplazo), con el atributo puesto y el foco recuperado si se perdió al
  // volar el nodo enfocado (sin foco dentro, Tab volvería a empezar arriba).
  restoreCursor() {
    if (!this.current) return
    const wasReplaced = !this.current.isConnected
    this.resolveCurrent()
    if (!this.current) return
    this.current.setAttribute(CURRENT, "")
    const active = document.activeElement
    if (wasReplaced && !this.finder && (active === document.body || !active?.isConnected)) {
      if (!this.current.hasAttribute("tabindex")) this.current.setAttribute("tabindex", "-1")
      this.current.focus({ preventScroll: true })
    }
  }

  // ── Flechas espaciales (elementos con data-shortcut-col) ──────────────

  // true si la tecla se resolvió en el plano de columnas (o no hay a dónde
  // ir en ese plano); false si el elemento no declara columna.
  spatial(key, event) {
    const item = this.current
    const col = item?.dataset.shortcutCol
    if (!col) return false

    const target = (key === "ArrowUp" || key === "ArrowDown")
      ? this.sameColumn(item, col, key === "ArrowDown" ? 1 : -1)
      : this.acrossColumns(item, col, key === "ArrowRight" ? 1 : -1)

    event.preventDefault()
    if (target) this.setCursor(target)
    return true
  }

  // Anterior/siguiente de la MISMA columna en toda la página (los equipos de
  // todos los traspasos, en orden).
  sameColumn(item, col, delta) {
    const column = this.items().filter((el) => el.dataset.shortcutCol === col)
    const index = column.indexOf(item)
    return column[index + delta] || null
  }

  // Cruce de columna dentro del traspaso: transfer ⇄ equip ⇄ router. Entre
  // equipo y router manda el grupo (la fila y el router al que reporta); sin
  // grupo o sin pareja, el primero de la columna vecina.
  acrossColumns(item, col, delta) {
    const transfer = item.closest(`${ITEM}[data-shortcut-col="transfer"]`)
    if (!transfer) return null
    const order = ["transfer", "equip", "router"]
    const nextCol = order[order.indexOf(col) + delta]
    if (!nextCol) return null
    if (nextCol === "transfer") return transfer

    const candidates = this.items().filter((el) => transfer.contains(el) && el.dataset.shortcutCol === nextCol)
    if (candidates.length === 0) {
      // Traspaso sin equipos: → salta directo al router. Router sin filas: ←
      // vuelve al traspaso.
      const beyond = order[order.indexOf(col) + 2 * delta]
      return beyond ? this.items().find((el) => transfer.contains(el) && el.dataset.shortcutCol === beyond) || null : null
    }
    const group = item.dataset.shortcutGroup
    return candidates.find((el) => group !== undefined && el.dataset.shortcutGroup === group) || candidates[0]
  }

  // Foco en un control FUERA de todo elemento del cursor (el rango del
  // gráfico, las pestañas, los botones de una card): ←/→ pasan al control
  // hermano dentro del mismo grupo (su contenedor más cercano con más de un
  // control), sin mover el cursor. Así el teclado recorre "24 h / 7 días /
  // 30 días" o las pestañas sin pasar por la tabla de eventos.
  stepSiblings(delta, event) {
    const active = document.activeElement
    if (!active || !active.matches(INTERACTIVE) || active.closest(ITEM) || active.closest(".tp-finder, .tp-modal")) return false

    let group = active.parentElement
    let siblings = []
    while (group && group !== document.body) {
      siblings = Array.from(group.querySelectorAll(INTERACTIVE)).filter((el) => el.getClientRects().length > 0)
      if (siblings.length > 1) break
      group = group.parentElement
    }
    if (siblings.length < 2 || group === document.body) return false

    event.preventDefault()
    const index = siblings.indexOf(active)
    siblings[(index + delta + siblings.length) % siblings.length].focus()
    return true
  }

  // Botones y links PROPIOS del elemento señalado (no de uno anidado), en
  // orden de documento: el link de la fila, editar, eliminar…
  focusablesIn(item) {
    return Array.from(item.querySelectorAll("a[href], button:not([disabled]), input:not([type=hidden]):not([disabled])"))
      .filter((el) => el.closest(ITEM) === item && el.getClientRects().length > 0)
  }

  // ← / →: el foco va al siguiente/anterior botón del elemento señalado.
  // Desde el elemento mismo (recién señalado), → va al primero y ← al último.
  stepWithin(delta, event) {
    const item = this.current
    if (!item) return false
    const focusables = this.focusablesIn(item)
    if (focusables.length === 0) return false

    event.preventDefault()
    const index = focusables.indexOf(document.activeElement)
    let next
    if (index === -1) {
      next = delta > 0 ? 0 : focusables.length - 1
    } else {
      next = (index + delta + focusables.length) % focusables.length
    }
    focusables[next].focus({ preventScroll: true })
    return true
  }

  openCurrent(event) {
    const item = this.current
    if (!item) return false

    // Con un botón o link enfocado (← / → o Tab) — del elemento o de
    // cualquier otro sitio de la página (el rango del gráfico, una pestaña)
    // — Enter es suyo: lo dispara el navegador. Solo con el foco en el
    // elemento mismo, o en ningún control, Enter abre lo señalado.
    const active = document.activeElement
    if (active && active !== item && active.matches(INTERACTIVE)) return false

    // El link propio del elemento (no el de un elemento anidado: la card del
    // traspaso abre el traspaso, no su primer equipo).
    const opener = Array.from(item.querySelectorAll("[data-shortcut-open]"))
      .find((el) => el.closest(ITEM) === item)
    const target = opener || (item.matches("a[href], [data-v2--row-link-url-value]") ? item : null)
    if (!target) return false

    event.preventDefault()
    this.activate(target)
    return true
  }

  // ── Capa 3: acciones ───────────────────────────────────────────────────

  // El botón que responde a `key`: en el elemento bajo el cursor, luego en
  // sus ancestros, luego en la página. Un botón dentro de OTRO elemento (una
  // fila hermana) nunca cuenta como de la página.
  resolveAction(key) {
    for (let scope = this.current; scope; scope = scope.parentElement?.closest(ITEM)) {
      const button = this.actionsIn(scope).find((el) => el.dataset.shortcutKey === key)
      if (button) return button
    }
    return this.pageActions().find((el) => el.dataset.shortcutKey === key) || null
  }

  // Botones PROPIOS del scope (no de elementos anidados).
  actionsIn(scope) {
    return Array.from(scope.querySelectorAll("[data-shortcut-key]"))
      .filter((el) => el.closest(ITEM) === scope && this.enabled(el))
  }

  pageActions() {
    return Array.from(document.querySelectorAll("[data-shortcut-key]"))
      .filter((el) => !el.closest(ITEM) && this.enabled(el))
  }

  enabled(el) {
    return !el.disabled && !el.classList.contains("disabled") && el.getClientRects().length > 0
  }

  runAction(key, event) {
    this.resolveCurrent()
    const button = this.resolveAction(key)
    if (!button) return false

    event.preventDefault()
    this.activate(button)
    return true
  }

  // Click sintético NO compuesto. `element.click()` emite un evento
  // `composed`, y Turbo (LinkInterceptor#clickEventIsSignificant) asume que
  // un click compuesto viene de un shadow DOM y mira `target.parentElement`:
  // el <a> del botón queda fuera y ni el `data-turbo-method` ni el
  // `data-turbo-frame` se procesan (con el mouse funciona porque el target
  // real es el <svg> del ícono y su padre SÍ es el link). Un MouseEvent con
  // `composed: false` llega con el propio elemento como target.
  activate(element) {
    element.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true, composed: false, view: window }))
  }

  // ── Buscador de elementos (`/`) ────────────────────────────────────────

  openFinder() {
    if (this.finder || !this.hasFinderTarget) return

    this.finderPrevious = this.current
    this.finder = this.finderTarget.content.firstElementChild.cloneNode(true)
    ;(document.fullscreenElement || document.body).appendChild(this.finder)
    this.matches = []
    this.finder.querySelector("input").focus()
  }

  closeFinder({ keep = true } = {}) {
    if (!this.finder) return

    this.finder.remove()
    this.finder = null
    this.matches = []
    this.clearAim()
    if (keep) {
      // Enter: el cursor se queda en la coincidencia y recibe el foco.
      if (this.current) this.setCursor(this.current)
    } else if (this.finderPrevious?.isConnected) {
      this.setCursor(this.finderPrevious)
    } else {
      this.clearCursor()
    }
    this.finderPrevious = null
  }

  // Qué se busca: los elementos del cursor (por su nombre) y las ACCIONES
  // de la página — botones y pestañas visibles, con el elemento al que
  // pertenecen como contexto ("Equipment 1 › Eliminar"). Así "elim equip 2"
  // llega al botón de eliminar de Equipment 2, y "vida" a la pestaña de vida
  // útil. Sin acentos ni mayúsculas, cada palabra por separado.
  entries() {
    const items = this.items()
    const list = items.map((el) => ({ kind: "item", el, label: this.nameOf(el).trim(), context: "" }))
    const actions = Array.from(document.querySelectorAll("main :is(a.btn, button.btn, .tp-tabs__list a, .tp-equip-range a)"))
      .filter((el) => !el.closest(".tp-modal, .tp-finder") && !el.disabled && !el.classList.contains("disabled"))
    actions.forEach((el) => {
      const label = this.labelOf(el)
      if (!label) return
      // Acción dentro del panel de una pestaña (gráficos del equipo/router):
      // entra aunque el panel esté oculto, con la pestaña como contexto
      // ("Vida útil › Vincular"); al elegirla se abre la pestaña primero.
      const panel = el.closest("[data-equip-tab]")
      const tab = panel && this.tabFor(panel.dataset.equipTab)
      if (tab && !panel.contains(tab)) {
        list.push({ kind: "action", el, label, context: this.labelOf(tab), tab })
        return
      }
      if (el.getClientRects().length === 0) return
      const owner = el.closest(ITEM)
      list.push({ kind: "action", el, label, context: owner ? this.nameOf(owner).trim() : "" })
    })
    return list
  }

  tabFor(key) {
    return Array.from(document.querySelectorAll(".tp-tabs__list a")).find((a) => a.dataset.equipTab === key) || null
  }

  filter(event) {
    const words = normalize(event.target.value).split(/\s+/).filter(Boolean)
    this.matches = words.length === 0 ? [] : this.entries().filter((entry) => {
      const text = normalize(`${entry.context} ${entry.label}`)
      return words.every((word) => text.includes(word))
    })
    this.matchIndex = 0
    // Mientras se escribe la página se queda quieta: desplazarla a cada
    // tecla marea. Se desplaza al recorrer las coincidencias con ↓/↑.
    this.showMatch({ scroll: false })
  }

  nameOf(item) {
    const opener = Array.from(item.querySelectorAll("[data-shortcut-open]")).find((el) => el.closest(ITEM) === item)
    return (opener || item).textContent
  }

  showMatch({ scroll = true } = {}) {
    const count = this.finder.querySelector(".tp-finder__count")
    const results = this.finder.querySelector(".tp-finder__results")
    results.replaceChildren()
    this.clearAim()
    if (this.matches.length === 0) {
      count.textContent = this.finder.querySelector("input").value ? count.dataset.finderEmpty : ""
      results.hidden = true
      this.clearCursor()
      return
    }
    count.textContent = `${this.matchIndex + 1}/${this.matches.length}`

    // Lista (hasta MAX_RESULTS, con la seleccionada siempre a la vista) por
    // nodos, nunca innerHTML: los nombres son datos del usuario.
    const start = Math.max(0, Math.min(this.matchIndex - 3, this.matches.length - MAX_RESULTS))
    this.matches.slice(start, start + MAX_RESULTS).forEach((entry, offset) => {
      const li = document.createElement("li")
      li.className = "tp-finder__result"
      li.setAttribute("role", "option")
      li.setAttribute("aria-selected", start + offset === this.matchIndex ? "true" : "false")
      if (entry.context) {
        const context = document.createElement("span")
        context.className = "tp-finder__context"
        context.textContent = `${entry.context} ›`
        li.appendChild(context)
      }
      li.appendChild(document.createTextNode(entry.label))
      results.appendChild(li)
    })
    results.hidden = false

    // El cursor acompaña: sobre el elemento, o sobre el dueño de la acción.
    // La acción misma (o su pestaña, si su panel está oculto) se enmarca
    // para que se vea qué disparará Enter.
    const selected = this.matches[this.matchIndex]
    const owner = selected.kind === "item" ? selected.el : selected.el.closest(ITEM)
    if (owner) {
      this.setCursor(owner, { scroll })
    } else {
      this.clearCursor()
    }
    if (selected.kind === "action") {
      const visible = selected.el.getClientRects().length > 0 ? selected.el : selected.tab
      if (visible) {
        visible.setAttribute(AIM, "")
        if (scroll) visible.scrollIntoView({ block: "nearest" })
      }
    }
  }

  clearAim() {
    document.querySelectorAll(`[${AIM}]`).forEach((el) => el.removeAttribute(AIM))
  }

  // Enter: un elemento queda señalado; una acción se dispara (con su
  // elemento señalado, para que lo que abra sepa de dónde viene).
  pickMatch() {
    const selected = this.matches[this.matchIndex]
    this.closeFinder({ keep: true })
    if (selected?.kind !== "action") return
    if (selected.tab) {
      // Primero la pestaña (muestra el panel), luego la acción ya visible.
      this.activate(selected.tab)
      setTimeout(() => this.activate(selected.el), 0)
    } else {
      this.activate(selected.el)
    }
  }

  // El foco se fue del buscador (click en la página, Tab): se cierra y el
  // cursor se queda donde iba. Si quedara abierto con el foco fuera, j/k
  // parecerían muertos: el teclado seguiría siendo del input.
  finderBlur(event) {
    if (this.finder?.contains(event.relatedTarget)) return
    // Al tick siguiente: si el foco se fue por un Enter/Escape propio, el
    // buscador ya no existe y no hay nada que cerrar.
    setTimeout(() => { if (this.finder && !this.finder.contains(document.activeElement)) this.closeFinder({ keep: true }) }, 0)
  }

  finderKeydown(event) {
    switch (event.key) {
      case "Escape":
        event.preventDefault()
        event.stopImmediatePropagation()
        this.closeFinder({ keep: false })
        break
      case "Enter":
        event.preventDefault()
        this.pickMatch()
        break
      case "ArrowDown":
      case "ArrowUp":
        if (this.matches.length === 0) return
        event.preventDefault()
        this.matchIndex = (this.matchIndex + (event.key === "ArrowDown" ? 1 : -1) + this.matches.length) % this.matches.length
        this.showMatch()
        break
    }
  }

  // ── Panel de ayuda ─────────────────────────────────────────────────────

  // Un modal de formulario o un confirm en pantalla: el teclado es de ellos.
  foreignModalOpen() {
    return Boolean(document.querySelector(".tp-modal:not(.tp-shortcuts)"))
  }

  toggle() {
    if (this.panel) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    if (this.panel || !this.hasTemplateTarget) return

    this.previouslyFocused = document.activeElement
    this.panel = this.templateTarget.content.firstElementChild.cloneNode(true)
    this.fillContextual(this.panel)
    ;(document.fullscreenElement || document.body).appendChild(this.panel)
    // El diálogo lleva tabindex=-1: enfocable por JS sin entrar al orden de
    // tabulación. Con el foco adentro Escape y Tab quedan en el panel.
    this.panel.querySelector(".tp-modal__dialog")?.focus({ preventScroll: true })
  }

  close() {
    if (!this.panel) return

    this.panel.remove()
    this.panel = null
    // El foco vuelve a donde estaba, si sigue en la página.
    if (this.previouslyFocused?.isConnected) this.previouslyFocused.focus({ preventScroll: true })
    this.previouslyFocused = null
  }

  // Secciones contextuales del panel: las acciones de los elementos de ESTA
  // página y las de la página misma, leídas del DOM. Cada sección viene
  // oculta en el template, con su <dl> vacío; sin botones, sigue oculta.
  fillContextual(panel) {
    const items = this.items()
    const itemActions = items.flatMap((item) => this.actionsIn(item))
    this.fillSection(panel, "item", itemActions)
    this.fillSection(panel, "page", this.pageActions())
    // Sin elementos en la página, el bloque del cursor (j/k/Enter) no aplica.
    if (items.length === 0) panel.querySelector("[data-shortcut-section=cursor]")?.remove()
  }

  fillSection(panel, name, buttons) {
    const section = panel.querySelector(`[data-shortcut-section=${name}]`)
    if (!section) return

    // Una fila por tecla+etiqueta: veinte "Editar" de veinte filas son UN
    // atajo. Orden por tecla para que la lista sea estable entre páginas.
    const seen = new Map()
    buttons.forEach((el) => {
      const key = el.dataset.shortcutKey
      const label = this.labelOf(el)
      const id = `${key}\u0000${label}`
      if (!seen.has(id)) seen.set(id, { key, label })
    })
    if (seen.size === 0) return

    const list = section.querySelector("dl")
    Array.from(seen.values())
      .sort((a, b) => a.key.localeCompare(b.key) || a.label.localeCompare(b.label))
      .forEach(({ key, label }) => list.appendChild(this.row(key, label)))
    section.hidden = false
  }

  labelOf(el) {
    const tabLabel = el.querySelector(".tp-tabs__label")
    return (
      el.getAttribute("aria-label") ||
      el.querySelector(".tp-btn__label")?.textContent ||
      (tabLabel && tabLabel.firstChild?.textContent) ||
      el.getAttribute("title") ||
      el.textContent
    ).trim()
  }

  // Misma estructura que las filas del template (ShortcutsComponent): así el
  // CSS de _shortcuts.scss las viste igual. Por nodos, nunca innerHTML: las
  // etiquetas pueden traer nombres de datos del usuario.
  row(key, label) {
    const row = document.createElement("div")
    row.className = "tp-shortcuts__row"
    const dt = document.createElement("dt")
    dt.className = "tp-shortcuts__keys"
    const kbd = document.createElement("kbd")
    kbd.className = "tp-shortcuts__key"
    kbd.textContent = key
    dt.appendChild(kbd)
    const dd = document.createElement("dd")
    dd.className = "tp-shortcuts__label"
    dd.textContent = label
    row.append(dt, dd)
    return row
  }
}
